import { useCallback, useEffect, useRef, useState } from 'react';
import { adminAimApi, type AimChatResponse } from '../../lib/api.js';

// Lane D2 (D5) — the AIM chat thread. Interaction model ported from the
// legacy AimChat (production/happy-tail components/aim/AimChat.tsx —
// design reference only): message bubbles, quick prompts on empty state,
// typing indicator while the tool loop runs, Enter-to-send. Rebuilt on
// the platform's ferro-* classes (NO Tailwind in this app — utility
// classes are inert) with 44px touch targets.

export interface AimUiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  toolsUsed?: string[];
  /** Reply hit max_tokens — render the "cut short" affordance (D5 AC). */
  truncated?: boolean;
  /** Local-only: this bubble is an error notice, styled differently. */
  isErrorNotice?: boolean;
}

const QUICK_PROMPTS = [
  "How's today looking?",
  'Any compliance issues?',
  "Who's working tomorrow?",
];

// Sawyer 17 ai-manager-help (F5) — the top-bar Help opens the panel in
// "help" mode: same chat, same endpoint, a how-to empty state. The first
// prompt is the missed clock-in Sawyer could not find (memo2 [21:03]).
const HELP_PROMPTS = [
  'How do I add a missed clock-in?',
  'How do I add a service to a visit?',
  'How do I print run cards?',
  'How do I record a check payment?',
];

const HELP_EMPTY_SUB =
  "I answer from the app's how-to guide and tell you plainly when something isn't in the app yet.";

/** Autofocus only where it can't raise a phone keyboard over the prompts (C10). */
const HELP_AUTOFOCUS_QUERY = '(min-width: 720px)';

const TOOL_LABELS: Record<string, string> = {
  get_today_summary: 'Checked the day overview',
  search_customer: 'Searched customers',
  search_dog: 'Searched dogs',
  check_schedule: 'Checked the schedule',
  get_staff_schedule: 'Checked staff schedules',
  create_booking: 'Created a booking',
  check_compliance: 'Reviewed compliance',
  get_revenue_summary: 'Pulled revenue stats',
  search_help: 'Checked the how-to guide', // Sawyer 17 ai-manager-help (F5)
};

function toolLabel(tool: string): string {
  return TOOL_LABELS[tool] ?? tool.replace(/_/g, ' ');
}

// #429 — a machine error code must never render in the chat thread.
// Known codes get typed copy; anything else code-shaped (snake_case with
// no spaces — internal_server_error from the global 500 handler, ai_error
// when the friendly body.message is missing, future codes) maps to a warm
// generic. Real sentences (from the server's `message` fields or the
// fetch wrapper's own retry copy) pass through untouched.
const GENERIC_ERROR_COPY = 'Something went wrong on our side — try that again.';

export function friendlyAimError(error: string | undefined): string {
  if (!error) return GENERIC_ERROR_COPY;
  if (error === 'ai_unavailable') {
    return 'AIM is currently offline — the AI service is not configured. Please contact your administrator.';
  }
  if (error === 'rate_limited') {
    return "You've hit the chat limit for now — give it a little while and try again.";
  }
  // Sawyer 17 ai-manager-help (F5, C6) — #1256 lets an owner switch staff
  // chat off (403 assistant_disabled); without this the Help button would
  // dead-end on the generic copy. Harmless before #1256 (never sent).
  if (error === 'assistant_disabled') {
    return 'The AI assistant is turned off for this business. An owner can turn it on in Settings → AI Assistant.';
  }
  // Code-shaped: all lowercase alphanumeric tokens joined by underscores.
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(error)) return GENERIC_ERROR_COPY;
  return error;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

type AimChatPanelProps = {
  apiUrl: string;
  conversationId: string | null;
  /** Fired when the server creates a conversation for the first message. */
  onConversationCreated: (id: string) => void;
  /** Fired after each completed exchange so the sidebar list can refresh. */
  onExchangeComplete: () => void;
  /** Sawyer 17 ai-manager-help (F5) — 'help' = the how-to empty state. */
  mode?: 'ops' | 'help';
};

export function AimChatPanel({
  apiUrl,
  conversationId,
  onConversationCreated,
  onExchangeComplete,
  mode = 'ops',
}: AimChatPanelProps): JSX.Element {
  const helpMode = mode === 'help';
  const [messages, setMessages] = useState<AimUiMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Sawyer 17 ai-manager-help (F5) — the id this panel's own first send
  // just created. The thread already holds that exchange (with its
  // "Checked the how-to guide" footer); re-reading it as history would
  // flash "Loading conversation…" and drop the footer, because the history
  // wire shape carries no tool list. ONE-SHOT: it covers only the single
  // conversationId change that follows onConversationCreated. The history
  // effect consumes it on its next run whatever the new id is, so a later
  // return to that conversation (New → pick it from the sidebar, in a panel
  // that never remounts) always reloads its history.
  const createdHereRef = useRef<string | null>(null);

  // Load history when switching conversations.
  useEffect(() => {
    const justCreatedHere = createdHereRef.current; // Sawyer 17 (F5)
    createdHereRef.current = null;
    if (!conversationId) {
      setMessages([]);
      return;
    }
    if (conversationId === justCreatedHere) return; // Sawyer 17 (F5)
    let cancelled = false;
    setHistoryLoading(true);
    void adminAimApi.getMessages(apiUrl, conversationId).then((res) => {
      if (cancelled) return;
      if (res.error) {
        // #711 — `error` is sanitized human copy now; the machine token
        // this map branches on lives on `code` (fallback keeps older wire
        // shapes and the fetch wrapper's own network copy working).
        setBanner(friendlyAimError(res.code ?? res.error));
      } else if (res.data) {
        setMessages(
          res.data.messages.map((m) => ({
            id: m.id,
            role: m.role === 'user' ? 'user' : 'assistant',
            content: m.content,
            createdAt: m.createdAt,
          })),
        );
      }
      setHistoryLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Sawyer 17 ai-manager-help (F5) — help mode lands ready to type on a
  // desk screen; on a phone the four prompts stay visible instead (C10).
  useEffect(() => {
    if (!helpMode) return;
    const wide =
      typeof window.matchMedia === 'function' && window.matchMedia(HELP_AUTOFOCUS_QUERY).matches;
    if (wide) inputRef.current?.focus();
  }, [helpMode]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;

      setBanner(null);
      setDraft('');
      setSending(true);
      setMessages((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          role: 'user',
          content: trimmed,
          createdAt: new Date().toISOString(),
        },
      ]);

      const res = await adminAimApi.chat(apiUrl, trimmed, conversationId ?? undefined);

      if (res.error || !res.data) {
        // 503 ai_unavailable / 429 rate_limited / 500 codes / network —
        // friendly banner, never a blank panel or a raw error code in the
        // bubble (D5 AC + #429). #711 — branch on `code` (the raw machine
        // token); `error` is sanitized prose now and would dead-end the
        // typed branches below.
        const friendly = friendlyAimError(res.code ?? res.error);
        setBanner(friendly);
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: friendly,
            createdAt: new Date().toISOString(),
            isErrorNotice: true,
          },
        ]);
      } else {
        const data: AimChatResponse = res.data;
        if (data.conversationId && data.conversationId !== conversationId) {
          createdHereRef.current = data.conversationId; // Sawyer 17 (F5)
          onConversationCreated(data.conversationId);
        }
        setMessages((prev) => [
          ...prev,
          {
            id: `resp-${Date.now()}`,
            role: 'assistant',
            content: data.message,
            createdAt: new Date().toISOString(),
            toolsUsed: data.toolsUsed,
            truncated: data.truncated === true,
          },
        ]);
        onExchangeComplete();
      }

      setSending(false);
      inputRef.current?.focus();
    },
    [apiUrl, conversationId, sending, onConversationCreated, onExchangeComplete],
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(draft);
    }
  }

  return (
    <section className="ferro-aim__panel" aria-label="AIM chat">
      {banner ? (
        <div className="ferro-aim__banner" role="alert" data-testid="aim-error-banner">
          <span>{banner}</span>
          <button
            type="button"
            className="ferro-aim__banner-dismiss"
            aria-label="Dismiss error"
            onClick={() => setBanner(null)}
          >
            ×
          </button>
        </div>
      ) : null}

      <div className="ferro-aim__thread" aria-live="polite" aria-label="Conversation">
        {historyLoading ? (
          <p className="ferro-aim__hint">Loading conversation…</p>
        ) : messages.length === 0 && !sending ? (
          helpMode ? (
            /* Sawyer 17 ai-manager-help (F5) — the how-to empty state. */
            <div className="ferro-aim__empty" data-testid="aim-help-empty-state">
              <p className="ferro-aim__empty-title">Ask how to do something</p>
              <p className="ferro-aim__empty-sub">
                {HELP_EMPTY_SUB}
              </p>
              <div className="ferro-aim__prompts">
                {HELP_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="ferro-aim__prompt"
                    data-testid="aim-help-prompt"
                    onClick={() => void send(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
          <div className="ferro-aim__empty" data-testid="aim-empty-state">
            <p className="ferro-aim__empty-title">Hi! I'm AIM, your AI manager.</p>
            <p className="ferro-aim__empty-sub">
              Ask me about today's bookings, staffing, or compliance.
            </p>
            <div className="ferro-aim__prompts">
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="ferro-aim__prompt"
                  onClick={() => void send(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          )
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`ferro-aim__msg ferro-aim__msg--${m.role}${m.isErrorNotice ? ' ferro-aim__msg--error' : ''}`}
            >
              <div className="ferro-aim__bubble">
                <p className="ferro-aim__bubble-text">{m.content}</p>
                {m.truncated ? (
                  <p className="ferro-aim__truncated" data-testid="aim-truncated-note">
                    Response was cut short — ask a follow-up to continue.
                  </p>
                ) : null}
                {m.toolsUsed && m.toolsUsed.length > 0 ? (
                  <p className="ferro-aim__tools">
                    {[...new Set(m.toolsUsed)].map(toolLabel).join(' · ')}
                  </p>
                ) : null}
              </div>
              <time className="ferro-aim__time" dateTime={m.createdAt}>
                {formatTime(m.createdAt)}
              </time>
            </div>
          ))
        )}
        {sending ? (
          <div className="ferro-aim__msg ferro-aim__msg--assistant" data-testid="aim-typing">
            <div className="ferro-aim__bubble ferro-aim__bubble--typing">
              <span className="ferro-aim__dot" />
              <span className="ferro-aim__dot" />
              <span className="ferro-aim__dot" />
            </div>
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      <div className="ferro-aim__composer">
        <textarea
          ref={inputRef}
          className="ferro-aim__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={helpMode ? 'Ask how to do something…' : 'Ask AIM anything…'}
          rows={1}
          disabled={sending}
          aria-label="Message AIM"
          onInput={(e) => {
            const t = e.target as HTMLTextAreaElement;
            t.style.height = 'auto';
            t.style.height = `${Math.min(t.scrollHeight, 140)}px`;
          }}
        />
        <button
          type="button"
          className="ferro-btn ferro-btn--primary ferro-aim__send"
          onClick={() => void send(draft)}
          disabled={!draft.trim() || sending}
          aria-label="Send message"
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </section>
  );
}
